import { flip, offset, Placement } from '@floating-ui/dom';
import { FloatingPortal } from '@floating-ui/react';
import { Unstable_Popup as Popup } from '@mui/base/Unstable_Popup';
import React, { PropsWithChildren, useRef, useState } from 'react';
import { SizeVariants } from '../../../theme/themes';
import { makeCSS } from '../../../utils/makeCSS';
import { useClassName } from '../../../utils/useClassName';
import { Panel } from '../../atoms/Panel';

import { Scrollbar } from '../../atoms/Scrollbar';
import { cx } from '@emotion/css';

/**
 * The ContextMenu is used to display and group together the different ContextMenuList components.
 * It is created by using the Panel compoment.
 */
export interface ContextMenuProps extends PropsWithChildren {
  children;
  /**The width of the contextMenu */
  width?: number | string;
  anchor?: HTMLElement;
  offset?: number;
  /** Use to check if the click is inside or outside of the popup element. */
  popupRef?: React.MutableRefObject<HTMLDivElement>;
  /**Maximum height for scrollbar */
  scrollbarHeight?: number;
  /**Gap between anchor and popup */
  popupGap?: number | string;
  /**If needed, do not use floating portal*/
  disableFloPortal?: boolean;
}

const useCSS = makeCSS(
  ({ theme, width = 64, popupGap = 0 }: ContextMenuProps & { theme }) => {
    return {
      root: {
        borderRadius: theme.borderRadius['2xl'],
        boxShadow:
          '0px 16px 80px 0px rgba(0, 0, 0, 0.20), 0px 4px 4px 0px rgba(0, 0, 0, 0.20);'
      },
      margins: {
        '> div': {
          marginBottom: 4
        },
        '>*:last-child': {
          marginBottom: 2
        }
      },
      width: {
        width: typeof width === 'string' ? width : theme.spacing[width]
      },
      scrollbar: {
        marginRight: 14
      },
      popupGap: {
        marginLeft: popupGap
      }
    };
  }
);

export function ContextMenu(
  props: ContextMenuProps & { placement?: Placement }
) {
  const { children, anchor, scrollbarHeight } = props;

  const classes = useCSS(props);

  const Container = anchor ? FloatingWrapper : React.Fragment;

  const [isScrollbarVisible, setScrollbarVisible] = useState(false);
  const handleScrollUpadate = (e: any) => {
    const { scrollHeight, clientHeight } = e;
    setScrollbarVisible(scrollHeight > clientHeight);
  };

  return (
    <Container {...props} anchor={anchor}>
      <div className={useClassName(classes.root, classes.width)}>
        <Panel
          cornerRadius={SizeVariants['2xl']}
          padding={2}
          backgroundColor={(theme) =>
            `linear-gradient(0deg, ${theme.color.overlay['02']} 0%, ${theme.color.overlay['02']} 100%), ${theme.color.background.neutral}`
          }
          borderColor={(theme) => theme.color.line['03']}
        >
          <Scrollbar
            autoHeight={true}
            colorScrollbar="main"
            autoHeightMax={scrollbarHeight}
            onUpdate={handleScrollUpadate}
          >
            <div
              className={cx(
                classes.margins,
                isScrollbarVisible ? classes.scrollbar : ''
              )}
            >
              {children}
            </div>
          </Scrollbar>
        </Panel>
      </div>
    </Container>
  );
}

export interface FloatingWrapperProps
  extends ContextMenuProps,
  Pick<ContextMenuProps, 'popupRef'> {
  anchor: HTMLElement;
  placement?: Placement;
  offset?: number;
}
const FloatingWrapper = (props: FloatingWrapperProps) => {
  const classes = useCSS(props);
  const { anchor, placement, popupRef, children, disableFloPortal = false } = props;
  return (
    <>
      {disableFloPortal ?
        <Popup
          open={true}
          placement={placement || 'bottom-start'}
          disablePortal
          strategy="fixed"
          middleware={[
            offset(props.offset === undefined ? 8 : props.offset),
            flip()
          ]}
          anchor={anchor}
          ref={popupRef}
        >
          <div className={useClassName(classes.popupGap)}>{children}</div>
        </Popup>
        :
        <FloatingPortal root={document.getElementById('select-dropdown')}>
          <Popup
            open={true}
            placement={placement || 'bottom-start'}
            disablePortal
            strategy="fixed"
            middleware={[
              offset(props.offset === undefined ? 8 : props.offset),
              flip()
            ]}
            anchor={anchor}
            ref={popupRef}
          >
            <div className={useClassName(classes.popupGap)}>{children}</div>
          </Popup>
        </FloatingPortal>}
    </>

  );
};
